import { memo, useCallback, RefObject } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { fromNow } from 'builder/utils/formatDate'
import { DocumentTypes } from 'builder/modules/constants'
import { CurrentDocument, PreviewRefProps } from '../../types'
import PaginationLayout from '../PaginationLayout'
import { SyncStatus } from '../../SyncStatus'
import { BottomBar, SyncStatusWrapper } from './styles'

type Props = {
  type: DocumentTypes
  currentDocument: CurrentDocument
  currentPage: number
  totalPagesNumber: number
  documentId: number
  previewRef: RefObject<PreviewRefProps | null>
  customAction?: () => void
  isOnline: boolean
  isSyncing: boolean
  isPrefilling?: boolean
}

export const BottomBarLayout = memo((props: Props) => {
  const {
    type,
    currentDocument,
    currentPage,
    totalPagesNumber,
    documentId,
    previewRef,
    customAction,
    isOnline,
    isPrefilling,
    isSyncing,
  } = props

  const updatedAt = currentDocument?.updatedAt
    ? fromNow(currentDocument.updatedAt, { addSuffix: true })
    : ''

  const hasPreviousPage = currentPage > 1
  const hasNextPage = currentPage < totalPagesNumber

  const handlePreviousPageClick = useCallback(() => {
    if (!previewRef.current) return
    previewRef.current.previousPage()
    trackInternalEvent(`change_${type}_preview_page`)
  }, [previewRef, type])

  const handleNextPageClick = useCallback(() => {
    if (!previewRef.current) return
    previewRef.current.nextPage()
    trackInternalEvent(`change_${type}_preview_page`)
  }, [previewRef, type])

  return (
    <BottomBar>
      <SyncStatusWrapper>
        <SyncStatus
          {...{
            updatedAt,
            documentId,
            isOnline,
            isPrefilling,
            isSyncing,
          }}
        />
      </SyncStatusWrapper>

      <PaginationLayout
        {...{
          hasPreviousPage,
          hasNextPage,
          currentPage,
          totalPagesNumber,
          handlePreviousPageClick: customAction || handlePreviousPageClick,
          handleNextPageClick: customAction || handleNextPageClick,
        }}
      />
    </BottomBar>
  )
})
